'use client';

import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useEffect, useState } from 'react';
import { BaseLineCirclePause } from '@/app/components/icons/BaseLineCirclePause';
import { BaseLinePlay2 } from '@/app/components/icons/BaseLineCirclePlay';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { BaseLineSubmit2 } from '@/app/components/icons/BaseLineSubmit2';
import { Loading } from '@/app/components/icons/Loading';
import { getAudioDisplayName, UploadedAudioCard } from '@/app/components/shared/UploadedAudioCard';
import { toast } from '@/app/components/ui/use-toast';
import { useAudioPlay, useAudioUpload } from '@/app/hooks';
import type { AudioUploadFile, FormAudioPrompt } from '@/app/types';
import { cn } from '@/app/utils';
import { ToolMediaUploadHeader } from './ToolMediaUploadHeader';

interface ToolAudioUploadProps {
  audioPrompt?: FormAudioPrompt | null;
  onAudioUpload: (file: AudioUploadFile) => void;
  onRemoveAudio?: () => void;
  label?: string;
  helperText?: string;
  className?: string;
  allowRemove?: boolean;
  maxDuration: number;
  minDuration: number;
  maxSize: number;
  normalizeDurationForUpload?: (durationSec: number) => number;
  /**
   * 传给原生文件选择的 accept，并与校验一致（传入非空时，仅允许匹配该规则的扩展名/MIME）。
   * 不传则使用内置默认（避免系统把视频 .mp4 当音频可选）。
   * 若业务需要接受视频容器中的音轨，可显式传入如 `.mp4,video/mp4,audio/mp4`。
   */
  accept?: string;
  compact?: boolean;
  nativePreview?: boolean;
  showHeader?: boolean;
  surfaceClassName?: string;
  uploadActionClassName?: string;
  uploadIcon?: ReactNode;
  helperTextInsideUpload?: boolean;
  playbackVariant?: AudioUploadPlaybackCardProps['visualVariant'];
}

interface AudioUploadPlaybackCardProps {
  audioSource: string;
  audioId: string;
  audioName: string;
  audioDuration: number;
  allowRemove: boolean;
  onSelectAudio: () => void;
  onRemove?: () => void;
  visualVariant?: 'default' | 'cover';
}

const AudioUploadPlaybackCard: FC<AudioUploadPlaybackCardProps> = ({
  audioSource,
  audioId,
  audioName,
  audioDuration,
  allowRemove,
  onSelectAudio,
  onRemove,
  visualVariant = 'default',
}) => {
  const t = useTranslations();
  const { isPlaying, progress, handlePlayPause, setProgress } = useAudioPlay({
    url: audioSource,
    id: audioId,
  });

  if (visualVariant === 'cover') {
    const clampedProgress = Math.min(progress, audioDuration);
    const progressPercent = Math.min((clampedProgress / audioDuration) * 100, 100);

    return (
      <div className="group relative flex h-full min-h-[88px] items-center gap-3 px-3">
        <button
          type="button"
          className="relative flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-8 text-white disabled:cursor-not-allowed disabled:text-system-white/20"
          onClick={handlePlayPause}
          disabled={!audioSource}
          aria-label={isPlaying ? t('tools.voiceClone.player.pause') : t('tools.voiceClone.player.play')}
        >
          <img
            src="/offline-digital-human-audio-cover.png"
            alt=""
            className="absolute inset-0 size-full object-cover"
          />
          <span className="absolute inset-0 bg-black/25" />
          <span className="relative z-10 flex items-center justify-center">
            {isPlaying ? <BaseLineCirclePause className="size-5" /> : <BaseLinePlay2 className="size-5" />}
          </span>
        </button>
        <div className="min-w-0 flex-1 self-stretch">
          <div className="flex h-full flex-col justify-center gap-1">
            <button
              type="button"
              className="block max-w-full truncate text-left text-sm text-system-white"
              onClick={onSelectAudio}
            >
              {audioName}
            </button>
            <span className="text-system-white64 text-xs">
              {formatAudioTime(clampedProgress)} / {formatAudioTime(audioDuration)}
            </span>
            <input
              type="range"
              min={0}
              max={audioDuration}
              step={0.1}
              value={clampedProgress}
              onChange={(event) => setProgress(Number(event.target.value))}
              aria-label={t('tools.offlineAvatar.audioUpload.progress')}
              style={{
                background: `linear-gradient(to right, white ${progressPercent}%, rgba(255, 255, 255, 0.2) ${progressPercent}%)`,
              }}
              className="[&::-webkit-slider-thumb]:-mt-0.5 h-1 w-full cursor-pointer appearance-none rounded-full [&::-moz-range-progress]:rounded-full [&::-moz-range-progress]:bg-white [&::-moz-range-thumb]:size-2 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white [&::-moz-range-track]:h-1 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-1 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:size-2 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-white"
            />
          </div>
        </div>
        {allowRemove && onRemove ? (
          <button
            type="button"
            aria-label={t('tools.offlineAvatar.audioUpload.remove')}
            className="absolute top-1 right-1 z-10 flex size-5 items-center justify-center rounded-full bg-black/55 text-system-white opacity-0 transition-all duration-200 hover:bg-black/75 group-hover:opacity-100"
            onClick={onRemove}
          >
            <BaseLineClose className="size-3" />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <UploadedAudioCard
      audioName={audioName}
      isPlaying={isPlaying}
      playDisabled={!audioSource}
      onSelectAudio={onSelectAudio}
      onPlayPause={handlePlayPause}
      onRemove={onRemove}
      showRemoveButton={allowRemove}
    />
  );
};

const formatAudioTime = (value: number) => {
  const totalSeconds = Math.max(Math.round(value), 0);
  return `${String(Math.floor(totalSeconds / 60)).padStart(2, '0')}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

export const ToolAudioUpload: FC<ToolAudioUploadProps> = ({
  audioPrompt,
  onAudioUpload,
  onRemoveAudio,
  label,
  helperText,
  className,
  allowRemove = true,
  maxDuration,
  minDuration,
  maxSize,
  normalizeDurationForUpload,
  accept,
  compact = false,
  nativePreview = false,
  showHeader = true,
  surfaceClassName,
  uploadActionClassName,
  uploadIcon,
  helperTextInsideUpload = false,
  playbackVariant = 'default',
}) => {
  const t = useTranslations();
  const [isClient, setIsClient] = useState(false);
  const { uploading, selectAudio } = useAudioUpload({
    maxDuration,
    minDuration,
    maxSize,
    normalizeDurationForUpload,
    accept,
    allowTempMode: true,
    onChange: (file) => {
      onAudioUpload(file);

      if (file.status === 'done' && file.file) {
        toast({
          message: t('sharedComponent.audioUploader.success.uploadSuccess'),
          type: 'success',
        });
      }
    },
  });

  useEffect(() => {
    setIsClient(true);
  }, []);

  const audioSource = audioPrompt?.prompt?.preview || audioPrompt?.prompt?.src || '';
  const audioName = getAudioDisplayName(
    audioPrompt?.prompt?.src || audioPrompt?.prompt?.preview,
    audioPrompt?.prompt?._tempFile?.name || audioPrompt?.prompt?.name,
  );
  const isUploading = uploading || audioPrompt?.status === 'uploading';
  const hasAudio = audioPrompt?.status === 'done' && !!audioPrompt.prompt;
  const audioDuration = Math.max(audioPrompt?.prompt?.duration ?? 0, 1);

  if (compact) {
    const openNativePreview = () => {
      if (nativePreview && audioSource) {
        window.open(audioSource, '_blank', 'noopener,noreferrer');
        return;
      }
      selectAudio();
    };

    return (
      <div className={cn('flex min-w-0', className)}>
        <div className="flex h-8 w-fit min-w-0 max-w-[160px] items-center gap-2 rounded-8 bg-system-white04 px-3 text-sm transition-colors hover:bg-system-white08">
          {isUploading ? (
            <div className="flex items-center gap-2 text-sm text-system-white/60">
              <Loading className="animate-spin" />
              <span>{t('create.createForm.upload.uploading')}</span>
            </div>
          ) : hasAudio && audioPrompt?.prompt ? (
            <>
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-left"
                onClick={openNativePreview}
              >
                <BaseLineMusic className="size-4 shrink-0 text-system-white/40" />
                <span className="min-w-0 truncate text-system-white">{audioName}</span>
              </button>
              {allowRemove && onRemoveAudio ? (
                <button
                  type="button"
                  aria-label={t('tools.offlineAvatar.audioUpload.remove')}
                  className="flex size-4 shrink-0 items-center justify-center rounded-4 text-system-white/40 transition-colors hover:bg-system-white08 hover:text-system-white"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemoveAudio();
                  }}
                >
                  <BaseLineClose className="size-3" />
                </button>
              ) : null}
            </>
          ) : (
            <button type="button" className="group flex items-center gap-2 text-sm" onClick={selectAudio}>
              <BaseLineMusic className="size-4 text-system-white/40 group-hover:text-system-white" />
              <span className="truncate text-system-white">{label}</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {showHeader ? (
        <ToolMediaUploadHeader icon={<BaseLineMusic className="size-5 text-system-white" />} title={label} />
      ) : null}

      <div
        className={cn(
          'relative w-full border border-system-white08 bg-system-white04 backdrop-blur-[30px]',
          hasAudio ? 'rounded-12' : 'flex min-h-[64px] flex-col justify-center rounded-16 px-4 py-4',
          surfaceClassName,
        )}
      >
        {isUploading ? (
          <div className="flex flex-col items-center justify-center gap-2 text-system-white/60">
            <Loading className="animate-spin text-2xl text-system-white" />
            <span className="text-xs leading-[1.4]">{t('create.createForm.upload.uploading')}</span>
          </div>
        ) : hasAudio && audioPrompt?.prompt ? (
          isClient ? (
            <AudioUploadPlaybackCard
              audioSource={audioSource}
              audioId={audioPrompt.id || 'tool-audio-upload'}
              audioName={audioName}
              audioDuration={audioDuration}
              allowRemove={allowRemove}
              onSelectAudio={selectAudio}
              onRemove={onRemoveAudio}
              visualVariant={playbackVariant}
            />
          ) : (
            <UploadedAudioCard
              audioName={audioName}
              playDisabled
              onSelectAudio={selectAudio}
              onRemove={onRemoveAudio}
              showRemoveButton={allowRemove}
            />
          )
        ) : (
          <button
            type="button"
            className={cn(
              'group flex h-full w-full cursor-pointer items-center justify-center gap-2',
              helperTextInsideUpload && 'flex-col gap-1',
              uploadActionClassName,
            )}
            onClick={selectAudio}
          >
            <span className="flex items-center gap-2">
              {uploadIcon ?? <BaseLineSubmit2 className="size-4 text-system-white" />}
              <span className="text-system-white/40 text-xs leading-[1.4] group-hover:text-system-white">
                {t('tools.uploadNewFile')}
              </span>
            </span>
            {helperTextInsideUpload && helperText ? (
              <span className="text-center text-system-white/40 text-xs leading-[1.4]">{helperText}</span>
            ) : null}
          </button>
        )}
      </div>

      {helperText && !helperTextInsideUpload ? (
        <div className="text-system-white/40 text-xs leading-[1.4]">{helperText}</div>
      ) : null}
    </div>
  );
};
